import React, { useEffect, useState, useCallback } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Api from "../Config/ApiConfig";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  Pending: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200" },
  InProcess: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200" },
  Resolved: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
  Solved: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
  Invalid: { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-200" },
  Complete: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" },
};

const Badge = ({ value }) => {
  const c = STATUS_COLORS[value] || { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.bg} ${c.text} ${c.border}`}>
      {value}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  switch (source) {
    case "partner_app":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
          <i className="fas fa-handshake text-[9px]"></i>
          <span>Partner App</span>
        </span>
      );
    case "customer_app":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-200">
          <i className="fas fa-mobile-alt text-[9px]"></i>
          <span>Customer App</span>
        </span>
      );
    case "web":
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
          <i className="fas fa-globe text-[9px]"></i>
          <span>Web</span>
        </span>
      );
  }
};

const AiLackBadgeWithHover = ({ summary }) => {
  const [show, setShow] = useState(false);

  if (!summary || summary.total_lacked === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        <i className="fas fa-check-circle text-[10px]"></i>
        <span>AI Covered</span>
      </span>
    );
  }

  const isPending = summary.pending_count > 0;

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button
        type="button"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border cursor-pointer transition-all ${
          isPending
            ? "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
            : "bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100"
        }`}
      >
        <i className={`fas ${isPending ? "fa-exclamation-triangle text-amber-600" : "fa-info-circle text-blue-600"} text-[11px]`}></i>
        <span>
          {summary.total_lacked} {summary.total_lacked === 1 ? "Lack" : "Lacks"}
          {isPending ? ` (${summary.pending_count} Pending)` : " (Handled)"}
        </span>
      </button>

      {/* Hover Popover Card */}
      {show && (
        <div
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 text-xs text-gray-700 z-50 pointer-events-none"
          style={{ filter: "drop-shadow(0 10px 15px rgba(0,0,0,0.1))" }}
        >
          <div className="flex items-center justify-between pb-2 mb-2.5 border-b border-gray-100">
            <div className="flex items-center gap-2 font-bold text-gray-900">
              <i className="fas fa-robot text-amber-500"></i>
              <span>AI Info Lack Insights</span>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
              {summary.total_lacked} Total {summary.total_lacked === 1 ? "Query" : "Queries"}
            </span>
          </div>

          <div className="space-y-2.5">
            <div>
              <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">Times AI Lacked Info</span>
              <p className="font-semibold text-gray-800 m-0 mt-0.5">
                AI could not answer <strong className="text-amber-700">{summary.total_lacked}</strong> {summary.total_lacked === 1 ? "time" : "times"} in this session
              </p>
            </div>

            <div>
              <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">Admin Actions Taken</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200 text-[11px]">
                  ✓ {summary.resolved_count} Resolved
                </span>
                {summary.invalid_count > 0 && (
                  <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold border border-gray-200 text-[11px]">
                    ✕ {summary.invalid_count} Invalid
                  </span>
                )}
                {summary.pending_count > 0 && (
                  <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-800 font-semibold border border-amber-200 text-[11px]">
                    ⚠️ {summary.pending_count} Pending
                  </span>
                )}
              </div>
            </div>

            {summary.latest_lacked_at && (
              <div>
                <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">Latest Lack Occurred</span>
                <p className="font-semibold text-gray-800 m-0 mt-0.5">
                  {moment(summary.latest_lacked_at).format("DD MMM YYYY, hh:mm A")}
                  <span className="text-gray-400 font-normal ml-1">({moment(summary.latest_lacked_at).fromNow()})</span>
                </p>
              </div>
            )}

            <div>
              <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider">Action on Latest Lack</span>
              <p className="m-0 mt-0.5 font-semibold">
                {summary.latest_action_taken ? (
                  <span className="text-emerald-700 inline-flex items-center gap-1">
                    <i className="fas fa-check-circle"></i>
                    Action Taken ({summary.latest_action_status})
                  </span>
                ) : (
                  <span className="text-amber-700 inline-flex items-center gap-1">
                    <i className="fas fa-clock"></i>
                    Action Pending (Not replied/handled yet)
                  </span>
                )}
              </p>
            </div>

            {summary.latest_lacked_question && (
              <div className="pt-2 border-t border-gray-100">
                <span className="text-gray-400 block text-[10px] uppercase font-bold tracking-wider mb-0.5">Latest Unanswered Question</span>
                <p className="italic text-gray-600 bg-gray-50 p-2 rounded border border-gray-100 m-0 line-clamp-2">
                  "{summary.latest_lacked_question}"
                </p>
              </div>
            )}
          </div>

          {/* Tooltip caret */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-solid border-t-white border-t-8 border-x-transparent border-x-8 border-b-0 drop-shadow-xs"></div>
        </div>
      )}
    </div>
  );
};

export default function ChatSupportList() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sourceFilter, setSourceFilter] = useState("all");
  const navigate = useNavigate();

  const fetchData = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const p = overrides.page ?? page;
      const s = overrides.source !== undefined ? overrides.source : sourceFilter;

      const url = s && s !== "all"
        ? `chat-support/list?page=${p}&limit=${limit}&source=${s}`
        : `chat-support/list?page=${p}&limit=${limit}`;

      const res = await Api.get(url);
      if (res.data?.success) {
        setData(res.data.data || []);
        setTotal(res.data.totalItems || 0);
      }
    } catch (error) {
      console.error("Fetch Data Error:", error);
      toast.error("Failed to load chat support requests.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sourceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSourceChange = (newSource) => {
    setSourceFilter(newSource);
    setPage(1);
    fetchData({ page: 1, source: newSource });
  };

  const handlePageClick = (e) => {
    const newPage = e.selected + 1;
    setPage(newPage);
    fetchData({ page: newPage });
  };

  const pageCount = Math.ceil(total / limit) || 1;

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">AI Chat Support</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Manage and monitor customer conversations, AI coverage, and missing info queries across all platforms
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-2xs font-medium">
                  Sorted by: <strong className="text-emerald-700">Latest Chat Activity</strong>
                </span>
                <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg font-semibold">
                  {total} total sessions
                </div>
              </div>
            </div>

            {/* Platform Source Tabs */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
              {[
                { id: "all", label: "All Sources", icon: "fa-layer-group" },
                { id: "web", label: "Web", icon: "fa-globe" },
                { id: "customer_app", label: "Customer App", icon: "fa-mobile-alt" },
                { id: "partner_app", label: "Partner App", icon: "fa-handshake" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleSourceChange(tab.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer ${
                    sourceFilter === tab.id
                      ? "bg-[#034328] text-white shadow-sm"
                      : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <i className={`fas ${tab.icon}`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible">
              <div className="user_list_wrap table-responsive overflow-visible">
                <table className="table table-striped align-middle" style={{ fontSize: 13, color: "#111827" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "20%" }}>User Details</th>
                      <th style={{ width: "12%" }}>Status</th>
                      <th style={{ width: "28%" }}>Latest Message</th>
                      <th style={{ width: "20%" }}>AI Coverage / Lacks</th>
                      <th style={{ width: "12%" }}>Last Active</th>
                      <th style={{ width: "8%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => {
                      const latestChatAt = row.latest_chat_at || row.createdAt;
                      const latestMsgText =
                        row.latest_chat?.user_message ||
                        row.latest_chat?.ai_response ||
                        "No messages yet";

                      return (
                        <tr key={row._id}>
                          <td>
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              <span className="font-semibold text-gray-900">{row.name}</span>
                              <SourceBadge source={row.source} />
                            </div>
                            <div className="text-xs text-gray-500">{row.email}</div>
                            {row.mobile && <div className="text-[11px] text-gray-400">{row.mobile}</div>}
                            {row.agent_id && (
                              <div className="text-[10px] text-purple-700 font-mono mt-0.5">
                                Agent: {row.agent_id.substring(0, 8)}...
                              </div>
                            )}
                          </td>

                          <td>
                            <Badge value={row.status} />
                          </td>

                          <td>
                            <div className="flex flex-col">
                              <p className="m-0 text-xs text-gray-700 font-medium truncate max-w-xs" title={latestMsgText}>
                                {row.latest_chat?.user_message ? (
                                  <span className="text-emerald-700 font-semibold mr-1">User:</span>
                                ) : (
                                  <span className="text-blue-600 font-semibold mr-1">AI:</span>
                                )}
                                {latestMsgText}
                              </p>
                              <span className="text-[11px] text-gray-400 mt-0.5">
                                {moment(latestChatAt).fromNow()}
                              </span>
                            </div>
                          </td>

                          <td>
                            <AiLackBadgeWithHover summary={row.ai_lack_summary} />
                          </td>

                          <td className="whitespace-nowrap text-xs text-gray-500">
                            <div>{moment(latestChatAt).format("DD MMM YYYY")}</div>
                            <div className="text-gray-400">{moment(latestChatAt).format("hh:mm A")}</div>
                          </td>

                          <td>
                            <button
                              onClick={() => navigate(`/chat-support/${row._id}`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-all cursor-pointer"
                            >
                              <span>View</span>
                              <i className="fas fa-arrow-right text-[10px]"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!loading && data.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-gray-400">
                          <i className="fas fa-comments text-4xl mb-3 block text-gray-300"></i>
                          No chat support requests found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination handlePageClick={handlePageClick} pageCount={pageCount} />
          </div>
        </div>
      </LoadingOverlay>
    </Layout>
  );
}
